import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@xxtract/ui';
import { vergelijkAandeel, vergelijkGetal, type Kerncijfers as KerncijfersWaarden, type Vergelijking } from '../kerncijfers';
import { naamVoorRegel } from '../naam';
import type { BijdrageRegel, Dekking, Persoonsverdeling } from '../contextkaarten';
import type { StapKeuze } from '../stapFilter';

/**
 * Story 33.3 — de drie kaarten onder aan het behandeltijdscherm.
 *
 * De vier kerncijfers die hier tot deze story bovenaan stonden beantwoordden de vraag "wat is het
 * getal". Deze drie beantwoorden een andere: **hoe hard is dit getal, en waar komt het vandaan.**
 * Daarom staan ze onderaan — na de aandachtslijst die het scherm leidt, na de grafiek en na de
 * ingeklapte tabel. Vier grote getallen bovenaan zouden de eerste blik weer wegtrekken van wat er
 * te sturen valt.
 *
 * Wat er met de vier oude kaarten gebeurd is:
 *
 * - Totale tijd, verdeling en uitgevoerde behandelingen zijn samen kaart 1 geworden. Ze gingen alle
 *   drie over dezelfde vraag — hoeveel is er gemeten — en stonden los van elkaar.
 * - Het gemiddelde per stuk mét het verschil tegen de norm is vervallen. Dat oordeel staat sinds
 *   story 33.2 per medewerker in de aandachtslijst, tegen de norm van die stap, mét wat het verschil
 *   kost; en de grafiek tekent hetzelfde gemiddelde als lijn met de norm erbij. Eén los groot getal
 *   erboven voegde daar niets aan toe en trok de blik juist weg.
 *
 * Drie dingen die dit onderdeel met opzet NIET doet:
 *
 * - Het telt geen regels om te zeggen hoeveel mensen er gewerkt hebben, en het wijst
 *   `person-removed` of `automated-system` nooit als persoon aan (epic 31 §2, AC-4a).
 * - Het maakt van een lege meting geen nul. Een streepje is geen weggelakt getal maar een
 *   afwezige meting, en "0 van de 30 dagen" is iets anders dan "niet te bepalen".
 * - Het velt geen oordeel over de dekking. Amber en rood zijn op dit scherm gereserveerd voor het
 *   oordeel tegen een norm; dekking is geen oordeel (AC-11).
 */

export interface ContextkaartenProps {
  cijfers: KerncijfersWaarden;
  /** Dezelfde meting over de vorige even lange periode, of `null` als die er niet is. */
  vorige: KerncijfersWaarden | null;
  /** De lengte van de periode in dagen; de vergelijking noemt hem met zoveel woorden. */
  dagen: number;
  /** Hoe de gemeten tijd over de mensen ligt (kaart 2). */
  verdeling: Persoonsverdeling;
  /** Waar het getal op rust (kaart 3), of `null` zodra de reeks er niet is (AC-13). */
  dekking: Dekking | null;
  /**
   * De reeks is nog ONDERWEG.
   *
   * Codereviewbevinding 30 augustus 2026: zonder dit onderscheid flitste bij elke periodewisseling
   * "de dekking is niet te bepalen" voorbij, terwijl er alleen nog niets binnen was. "Wordt
   * opgehaald" en "niet te bepalen" zijn twee verschillende dingen, en het tweede is een uitspraak.
   */
  dekkingBezig?: boolean;
  /**
   * De stap waartoe het scherm beperkt is (story 33.1), of AFWEZIG voor "beide".
   *
   * Beperkt betekent WEGLATEN en niet doorstrepen: de verdeling over de twee stappen verdwijnt dan
   * helemaal — op één stap valt er niets te verdelen (AC-3).
   */
  stap?: StapKeuze;
}

const SLEUTEL = 'handlingTime.contextkaarten';

/** Het pijltje dat de richting zonder woorden laat zien. Puur decoratie; de tekst zegt hetzelfde. */
const PIJL: Record<Vergelijking['soort'], string | null> = {
  geen: null,
  'toen-niets-gemeten': null,
  'vrijwel-gelijk': 'mdi-trending-neutral',
  meer: 'mdi-trending-up',
  minder: 'mdi-trending-down',
};

/**
 * De opmaak van één getal, of van het streepje dat ervoor in de plaats staat.
 *
 * Een streepje krijgt de gedempte kleur en het gewone gewicht: "hier is niets gemeten" is geen
 * uitkomst en hoort dus ook niet als een uitkomst te schreeuwen.
 */
function waardeKlasse(leeg: boolean, basis: string): string {
  return leeg ? `${basis} font-normal text-[var(--foreground-muted)]` : `${basis} font-semibold text-[var(--foreground)]`;
}

/**
 * Eén kaart. Het merkteken `data-contextkaart` staat er zodat een toets de kaarten kan tellen
 * zonder per ongeluk ook de onderdelen bínnen een kaart mee te tellen.
 *
 * HET OPSCHRIFT IS EEN KOP en geen los stukje tekst — een bevinding uit de epic-brede review van
 * 30 augustus 2026. De drie kaarten droegen helemaal geen kop, en wie op koppen door de pagina
 * springt sloeg ze dus over. Niveau drie, want ze staan ónder de kop van het blok hieronder; de
 * letters blijven precies dezelfde.
 */
function Kaart({ children, icon, iconColor, label, name }: {
  children: ReactNode;
  icon: string;
  iconColor: string;
  label: string;
  name: string;
}) {
  return (
    <Card className="flex flex-col p-5" data-contextkaart={name} data-testid={`contextkaart-${name}`}>
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-xs font-medium text-[var(--foreground-muted)]" data-testid={`contextkaart-${name}-label`}>
          {label}
        </h3>
        <span aria-hidden="true" className={`mdi ${icon} text-base ${iconColor}`} />
      </div>
      {children}
    </Card>
  );
}

export function Contextkaarten({ cijfers, vorige, dagen, verdeling, dekking, dekkingBezig = false, stap }: ContextkaartenProps) {
  const { t, i18n } = useTranslation();
  const taal = i18n.language;
  const streepje = t(`${SLEUTEL}.geenMeting`);
  /** Het woord voor de gekozen stap, of `null` op "Beide" — dan valt er geen stap te noemen. */
  const stapWoord = stap === undefined ? null : t(`${SLEUTEL}.${stap}`);

  const heelGetal = useMemo(() => new Intl.NumberFormat(taal, { maximumFractionDigits: 0 }), [taal]);
  const eenDecimaal = useMemo(
    () => new Intl.NumberFormat(taal, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
    [taal],
  );

  /** Uren en minuten, of een streepje zodra er niets gemeten is. */
  function alsUren(minuten: number | null): string {
    if (minuten === null) return streepje;
    let uren = Math.floor(minuten / 60);
    let rest = Math.round(minuten - uren * 60);
    if (rest === 60) { uren += 1; rest = 0; }
    return t(`${SLEUTEL}.gemeten.urenMinuten`, { uren: heelGetal.format(uren), minuten: rest });
  }

  function verschilVan(kies: (c: KerncijfersWaarden) => number | null): Vergelijking {
    if (vorige === null) return { soort: 'geen' };
    return vergelijkGetal(kies(cijfers), kies(vorige));
  }

  const verschilTotaal = verschilVan((c) => c.totaleMinuten);
  const verschilBehandelingen = verschilVan((c) => c.uitgevoerdeBehandelingen);
  // De verdeling verschilt in PROCENTPUNTEN; van 55% naar 57% is twee procentpunt meer, niet 3,6%
  // meer. Zonder vorige periode valt er niets te vergelijken, en dat is iets anders dan "toen niets
  // gemeten".
  const verschilVerdeling: Vergelijking = vorige === null
    ? { soort: 'geen' }
    : vergelijkAandeel(cijfers.vastleggen.aandeel, vorige.vastleggen.aandeel);

  /**
   * "In de dertig dagen ervoor is niets gemeten" staat er ÉÉN KEER, onder de kaarten.
   *
   * Dat is vandaag de normale toestand — de meting loopt pas sinds 25 augustus — en op de vier oude
   * kaarten stond die zin dus vier keer onder elkaar. Vier keer dezelfde mededeling leest als vier
   * mededelingen (AC-15).
   *
   * ALLEMAAL, en niet "minstens één", en dat is een codereviewbevinding van 30 augustus 2026. Met
   * `some` ging de regel al aan zodra ÉÉN vergelijking hem droeg: had de vorige periode wél minuten
   * maar geen behandelingen, dan stond er "12,0% meer dan de 30 dagen ervoor" met daar direct onder
   * "in de 30 dagen ervoor is niets gemeten". Twee zinnen die elkaar tegenspreken in één kaart.
   * Draagt niet elke vergelijking dezelfde mededeling, dan zegt elk getal gewoon zijn eigen waarheid
   * op zijn eigen plek — en dan herhaalt er niets.
   */
  const zichtbaar: Vergelijking[] = [verschilTotaal, verschilBehandelingen, ...(stap === undefined ? [verschilVerdeling] : [])];
  const toenNietsGemeten = zichtbaar.every((uitkomst) => uitkomst.soort === 'toen-niets-gemeten');

  /** De vergelijking bij één getal — behalve wanneer hij in de gedeelde regel eronder al staat. */
  function Verschil({ uitkomst, testId }: { uitkomst: Vergelijking; testId: string }) {
    if (toenNietsGemeten) return null;

    function tekst(): string {
      if (uitkomst.soort === 'geen') return t(`${SLEUTEL}.vergelijking.geen`);
      if (uitkomst.soort === 'toen-niets-gemeten') return t(`${SLEUTEL}.vergelijking.toenNietsGemetenHier`, { dagen });
      if (uitkomst.soort === 'vrijwel-gelijk') return t(`${SLEUTEL}.vergelijking.vrijwelGelijk`, { dagen });

      const waarde = eenDecimaal.format(uitkomst.waarde ?? 0);
      const inPunten = uitkomst.eenheid === 'procentpunt';
      const naam = inPunten
        ? (uitkomst.soort === 'meer' ? 'puntenMeer' : 'puntenMinder')
        : (uitkomst.soort === 'meer' ? 'meer' : 'minder');
      return t(`${SLEUTEL}.vergelijking.${naam}`, { waarde, dagen });
    }

    const pijl = PIJL[uitkomst.soort];

    return (
      <p className="mt-2 flex items-center gap-1 text-xs text-[var(--foreground-muted)]" data-testid={testId}>
        {pijl !== null && <span aria-hidden="true" className={`mdi ${pijl} text-sm`} />}
        {tekst()}
      </p>
    );
  }

  /**
   * KAART 1 — gemeten deze periode.
   *
   * Hoeveel er gemeten is, en hoe dat over de twee stappen ligt. De verdeling bestaat ALLEEN tussen
   * twee stappen: op één gekozen stap valt er niets te verdelen, en "100,0% · —" zou een verhouding
   * tonen waarvan de andere helft er met opzet niet is (AC-3).
   */
  const breedteVastleggen = cijfers.vastleggen.aandeel ?? 0;
  const breedteValideren = cijfers.valideren.aandeel ?? 0;

  const kaartGemeten = (
    <Kaart icon="mdi-clock-outline" iconColor="text-[var(--primary)]" label={t(`${SLEUTEL}.gemeten.titel`)} name="gemeten">
      <p className={waardeKlasse(cijfers.totaleMinuten === null, 'mt-2 text-2xl tabular-nums')} data-testid="contextkaart-gemeten-uren">
        {alsUren(cijfers.totaleMinuten)}
      </p>
      <p className="text-xs tabular-nums text-[var(--foreground-muted)]" data-testid="contextkaart-gemeten-minuten">
        {cijfers.totaleMinuten === null
          ? streepje
          : t(`${SLEUTEL}.gemeten.minuten`, {
            count: Math.round(cijfers.totaleMinuten),
            waarde: heelGetal.format(cijfers.totaleMinuten),
          })}
      </p>
      <Verschil testId="contextkaart-gemeten-vergelijking" uitkomst={verschilTotaal} />
      <p className="mt-3 text-xs tabular-nums text-[var(--foreground-muted)]" data-testid="contextkaart-gemeten-behandelingen">
        {cijfers.uitgevoerdeBehandelingen === null
          ? streepje
          : t(`${SLEUTEL}.gemeten.behandelingen`, {
            count: cijfers.uitgevoerdeBehandelingen,
            waarde: heelGetal.format(cijfers.uitgevoerdeBehandelingen),
          })}
      </p>
      {/* De vergelijking staat BIJ het getal waar hij over gaat en niet onderaan de kaart; daar
          stond hij onder het hele verdelingsblok, los van de behandelingen erboven
          (codereviewbevinding 30 augustus 2026). */}
      <Verschil testId="contextkaart-gemeten-behandelingen-vergelijking" uitkomst={verschilBehandelingen} />

      {stap === undefined && (
        <div className="mt-4 border-t border-[var(--border-soft)] pt-3">
          <p className="text-xs tabular-nums">
            <span
              className={waardeKlasse(cijfers.vastleggen.aandeel === null, '')}
              data-testid="contextkaart-gemeten-verdeling-vastleggen"
            >
              {t(`${SLEUTEL}.gemeten.deel`, {
                stap: t(`${SLEUTEL}.vastleggen`),
                waarde: cijfers.vastleggen.aandeel === null ? streepje : `${eenDecimaal.format(cijfers.vastleggen.aandeel)}%`,
              })}
            </span>
            <span className="px-1.5 font-normal text-[var(--foreground-muted)]">{t(`${SLEUTEL}.scheider`)}</span>
            <span
              className={waardeKlasse(cijfers.valideren.aandeel === null, '')}
              data-testid="contextkaart-gemeten-verdeling-valideren"
            >
              {t(`${SLEUTEL}.gemeten.deel`, {
                stap: t(`${SLEUTEL}.valideren`),
                waarde: cijfers.valideren.aandeel === null ? streepje : `${eenDecimaal.format(cijfers.valideren.aandeel)}%`,
              })}
            </span>
          </p>
          {/* DE BALK IS DECORATIE: allebei de percentages staan er hierboven als tekst. */}
          <div
            aria-hidden="true"
            className="mt-2 flex h-1.5 overflow-hidden rounded-full bg-[var(--border-soft)]"
            data-testid="contextkaart-gemeten-verdeling-balk"
          >
            <div className="bg-[var(--primary)]" style={{ width: `${breedteVastleggen}%` }} />
            <div className="bg-[var(--secondary)]" style={{ width: `${breedteValideren}%` }} />
          </div>
          <p className="mt-2 text-[11px] leading-snug text-[var(--foreground-muted)]" data-testid="contextkaart-gemeten-verdeling-uitleg">
            {t(`${SLEUTEL}.gemeten.verdelingUitleg`)}
          </p>
          <Verschil testId="contextkaart-gemeten-verdeling-vergelijking" uitkomst={verschilVerdeling} />
        </div>
      )}
    </Kaart>
  );

  /**
   * KAART 2 — verdeling over de mensen.
   *
   * Welk deel van de tijd van de grootste bijdrager komt, met een staafje per persoon eronder. De
   * staafjes zijn decoratie voor het OOG maar niet voor een schermlezer: het percentage staat in de
   * toegankelijke naam van elk staafje, zodat de volgorde en de verhouding ook zonder beeld
   * overkomen (AC-5).
   */
  function staafje(regel: BijdrageRegel) {
    const naam = naamVoorRegel(regel, t);
    const sleutel = `contextkaart-mensen-regel-${regel.userId}`;
    const percentage = `${eenDecimaal.format(regel.aandeel)}%`;

    return (
      <li className="flex items-center gap-2" data-testid={sleutel} key={regel.userId}>
        <span className="w-32 flex-none truncate text-xs text-[var(--foreground)]" data-testid={`${sleutel}-naam`}>{naam}</span>
        <span
          aria-label={t(`${SLEUTEL}.mensen.staaf`, { naam, aandeel: percentage })}
          className="block h-1.5 flex-1 overflow-hidden rounded-full bg-[var(--border-soft)]"
          data-testid={`${sleutel}-balk`}
          role="img"
        >
          <span className="block h-full rounded-full bg-[var(--primary)]" style={{ width: `${Math.min(100, regel.aandeel)}%` }} />
        </span>
        {/* Hetzelfde percentage als in de toegankelijke naam hierboven; voor een schermlezer dus
            verborgen, want twee keer hetzelfde getal is geen extra informatie. */}
        <span aria-hidden="true" className="w-14 flex-none text-right text-xs tabular-nums text-[var(--foreground-muted)]" data-testid={`${sleutel}-aandeel`}>
          {percentage}
        </span>
      </li>
    );
  }

  const kaartMensen = (
    <Kaart icon="mdi-account-group-outline" iconColor="text-[var(--secondary)]" label={t(`${SLEUTEL}.mensen.titel`)} name="mensen">
      {verdeling.soort === 'geen-meting' ? (
        <p className="mt-2 text-sm leading-relaxed text-[var(--foreground-muted)]" data-testid="contextkaart-mensen-leeg">
          {t(`${SLEUTEL}.mensen.leeg`)}
        </p>
      ) : (
        <>
          {verdeling.grootste !== null && (
            <p className="mt-2 text-sm leading-snug text-[var(--foreground)]" data-testid="contextkaart-mensen-grootste">
              {t(`${SLEUTEL}.mensen.grootste`, {
                naam: naamVoorRegel(verdeling.grootste, t),
                aandeel: `${eenDecimaal.format(verdeling.grootste.aandeel)}%`,
              })}
            </p>
          )}

          {/* EÉN PERSOON KRIJGT GEEN LIJST: één staafje van honderd procent verbeeldt geen
              verdeling, het herhaalt alleen het totaal (AC-7). */}
          {verdeling.regels.length > 1 && (
            <ul className="mt-3 space-y-1.5" data-testid="contextkaart-mensen-lijst">
              {verdeling.regels.map((regel) => staafje(regel))}
            </ul>
          )}

          {/* DE TIJD DIE AAN NIEMAND TOE TE WIJZEN IS verdwijnt niet: hij staat hier, met de reden
              erbij, zodat de staafjes hierboven niet stilzwijgend minder dan het geheel beslaan. */}
          {verdeling.buitenBeeld.map((buiten) => (
            <p
              className="mt-2 text-[11px] leading-snug text-[var(--foreground-muted)]"
              data-testid={`contextkaart-mensen-buiten-${buiten.userNameOutcome}`}
              key={buiten.userNameOutcome}
            >
              {t(`${SLEUTEL}.mensen.buiten`, {
                reden: naamVoorRegel({ userNameOutcome: buiten.userNameOutcome }, t),
                aandeel: `${eenDecimaal.format(buiten.aandeel)}%`,
              })}
            </p>
          ))}

          {/* GEEN AANTAL MENSEN in deze zin, en dat is een codereviewbevinding van 30 augustus 2026.
              Hier stond "De tijd is over 3 medewerkers verdeeld", geteld op het aantal REGELS — en
              één regel is niet één mens: `person-removed` vouwt er een onbekend aantal samen (epic
              31 §2). Het scherm telt dus nergens regels om te zeggen hoeveel mensen er gewerkt
              hebben; de zin zegt hoe de tijd LIGT, en de staafjes laten zien over wie. */}
          <p className="mt-3 text-[11px] leading-snug text-[var(--foreground-muted)]" data-testid="contextkaart-mensen-uitleg">
            {t(`${SLEUTEL}.mensen.uitleg.${verdeling.soort}`)}
          </p>
        </>
      )}
    </Kaart>
  );

  /**
   * KAART 3 — waar dit getal op rust.
   *
   * Hoeveel tijdvakken binnen de periode een meting bevatten, tegen het totaal. De EENHEID is niet
   * altijd de dag: hij volgt de korrel die het scherm werkelijk opgehaald heeft — dag tot en met
   * 31 dagen, week daarboven, maand boven 183 dagen (AC-12a).
   *
   * GEEN OORDEELKLEUR (AC-11): het beeld hieronder gebruikt dezelfde twee kleuren als elke andere
   * gegevensweergave op dit scherm, en nergens amber of rood.
   *
   * EN HIJ NOEMT DE GEKOZEN STAP, zodra het filter op één stap staat — een bevinding uit de
   * epic-brede review van 30 augustus 2026. De dekking wordt gerekend op de reeks ZOALS HET FILTER
   * HEM SNIJDT, dus op `stap=valideren` telt hij alleen valideerdagen. Zonder het woord "valideren"
   * erbij las "Geen enkele dag in deze periode bevat een meting" als een uitspraak over de hele
   * periode, terwijl er elke dag gemeten was — alleen in de andere stap. Dat is precies de leesfout
   * die AC-13 verbiedt, één stap verderop.
   */
  const kaartDekking = (
    <Kaart icon="mdi-calendar-check-outline" iconColor="text-[var(--primary)]" label={t(`${SLEUTEL}.dekking.titel`)} name="dekking">
      {dekking === null && dekkingBezig ? (
        <p className="mt-2 text-sm leading-relaxed text-[var(--foreground-muted)]" data-testid="contextkaart-dekking-bezig">
          {t(`${SLEUTEL}.dekking.bezig`)}
        </p>
      ) : dekking === null ? (
        <p className="mt-2 text-sm leading-relaxed text-[var(--foreground-muted)]" data-testid="contextkaart-dekking-onbekend">
          {t(`${SLEUTEL}.dekking.onbekend`)}
        </p>
      ) : (
        <>
          <p className="mt-2 text-2xl font-semibold tabular-nums text-[var(--foreground)]" data-testid="contextkaart-dekking-waarde">
            {t(`${SLEUTEL}.dekking.waarde`, {
              gemeten: heelGetal.format(dekking.gemeten),
              totaal: heelGetal.format(dekking.totaal),
              eenheid: t(`${SLEUTEL}.dekking.eenheid.${dekking.korrel}`, { count: dekking.totaal }),
            })}
            {/* WAARIN er gemeten is, in kleinere letters achter het getal. Het staat in dezelfde
                alinea en niet eronder, want het is één zin: "4 van de 30 dagen met een meting in
                vastleggen". Los eronder zou het als een tweede mededeling lezen. */}
            {stapWoord !== null && (
              <>
                {' '}
                <span className="text-sm font-normal text-[var(--foreground-muted)]" data-testid="contextkaart-dekking-instap">
                  {t(`${SLEUTEL}.dekking.inStap`, { stap: stapWoord })}
                </span>
              </>
            )}
          </p>
          {/* EEN STREEPJE PER TIJDVAK (AC-9). Decoratie voor het oog: het getal erboven zegt
              hetzelfde, en een schermlezer krijgt dus niet dertig losse streepjes voorgelezen. */}
          <div aria-hidden="true" className="mt-3 flex flex-wrap gap-[3px]" data-testid="contextkaart-dekking-beeld">
            {dekking.tijdvakken.map((tijdvak) => (
              <span
                className={`h-3 w-1.5 rounded-sm ${tijdvak.gemeten ? 'bg-[var(--primary)]' : 'bg-[var(--border-soft)]'}`}
                data-gemeten={tijdvak.gemeten ? 'true' : 'false'}
                key={tijdvak.label}
              />
            ))}
          </div>
          {/* EEN PERIODE VAN ÉÉN TIJDVAK krijgt een eigen zin: "de helft of meer van de dag" is
              rekenkundig waar en leest als onzin (codereviewbevinding 30 augustus 2026). Bij één
              tijdvak kan `weinig` trouwens niet ontstaan — één gemeten van één is altijd ruim. */}
          <p className="mt-3 text-[11px] leading-snug text-[var(--foreground-muted)]" data-testid="contextkaart-dekking-uitleg">
            {t(
              `${SLEUTEL}.dekking.uitleg.${
                dekking.totaal === 1 && dekking.soort !== 'geen' ? 'enige' : dekking.soort
              }${stapWoord === null ? '' : 'Stap'}`,
              {
                eenheid: t(`${SLEUTEL}.dekking.eenheid.${dekking.korrel}`, { count: dekking.totaal }),
                enkelvoud: t(`${SLEUTEL}.dekking.eenheid.${dekking.korrel}`, { count: 1 }),
                ...(stapWoord === null ? {} : { stap: stapWoord }),
              },
            )}
          </p>
          {dekking.rand > 0 && (
            <p className="mt-1 text-[11px] leading-snug text-[var(--foreground-muted)]" data-testid="contextkaart-dekking-rand">
              {t(`${SLEUTEL}.dekking.rand`, {
                count: dekking.rand,
                enkelvoud: t(`${SLEUTEL}.dekking.eenheid.${dekking.korrel}`, { count: 1 }),
                meervoud: t(`${SLEUTEL}.dekking.eenheid.${dekking.korrel}`, { count: 2 }),
              })}
            </p>
          )}
        </>
      )}
    </Kaart>
  );

  return (
    <section data-testid="handling-time-contextkaarten">
      {/* DE KOP VAN HET BLOK. Hij zegt wat deze drie kaarten samen beantwoorden — niet "wat is het
          getal" maar hoe hard het is — en hij geeft de drie kaarten eronder een plek in de
          koppenstructuur (epic-brede review 30 augustus 2026). */}
      <h2 className="mb-3 text-sm font-semibold text-[var(--foreground)]">{t(`${SLEUTEL}.titel`)}</h2>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {kaartGemeten}
        {kaartMensen}
        {kaartDekking}
      </div>
      {toenNietsGemeten && (
        <p className="mt-2 text-xs text-[var(--foreground-muted)]" data-testid="contextkaarten-toen-niets-gemeten">
          {t(`${SLEUTEL}.vergelijking.toenNietsGemeten`, { dagen })}
        </p>
      )}
    </section>
  );
}
